import { getAuth } from "firebase/auth";

class KappaMLAPI {
  constructor() {
    if (!import.meta.env.VITE_KAPPAML_API_URL) {
      console.error(
        "VITE_KAPPAML_API_URL is not defined. Please check your environment configuration.",
      );
    }
    this.baseURL =
      import.meta.env.VITE_KAPPAML_API_URL || "https://api.kappaml.com/v1";
    this.auth = getAuth();
  }

  async getHeaders() {
    const token = await this.auth.currentUser?.getIdToken();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  async handleResponse(response) {
    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || "An error occurred";
      } catch {
        errorMessage = "An unexpected error occurred";
      }
      throw new Error(errorMessage);
    }
    return response.json();
  }

  // User Profile
  async getUserProfile() {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/users/me`, {
      headers,
    });
    return this.handleResponse(response);
  }

  async updateProfile(profileData) {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/users/me`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(profileData),
    });
    return this.handleResponse(response);
  }

  // API Keys
  async listApiKeys() {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/api-keys`, {
      headers,
    });
    return this.handleResponse(response);
  }

  async createApiKey(name) {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/api-keys`, {
      method: "POST",
      headers,
      body: JSON.stringify({ name }),
    });
    return this.handleResponse(response);
  }

  async deleteApiKey(keyId) {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/api-keys/${keyId}`, {
      method: "DELETE",
      headers,
    });
    return this.handleResponse(response);
  }

  // Models
  async listModels() {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/models`, {
      headers,
    });
    return this.handleResponse(response);
  }

  async getModel(modelId) {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/models/${modelId}`, {
      headers,
    });
    return this.handleResponse(response);
  }

  async getModelMetrics(modelId, instance) {
    const headers = await this.getHeaders();
    const params = instance ? `?instance=${encodeURIComponent(instance)}` : "";
    const response = await fetch(
      `${this.baseURL}/models/${modelId}/metrics${params}`,
      { headers },
    );
    return this.handleResponse(response);
  }

  async getHistoricalMetrics(modelId, timeRange, instance) {
    const headers = await this.getHeaders();
    const params = new URLSearchParams({ timerange: timeRange });
    if (instance) params.set("instance", instance);
    const response = await fetch(
      `${this.baseURL}/models/${modelId}/metrics/history?${params}`,
      { headers },
    );
    return this.handleResponse(response);
  }

  async getModelCheckpoints(modelId) {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${this.baseURL}/models/${modelId}/checkpoints`,
      {
        headers,
      },
    );
    return this.handleResponse(response);
  }

  async createModel(modelData) {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/models`, {
      method: "POST",
      headers,
      body: JSON.stringify(modelData),
    });
    return this.handleResponse(response);
  }

  async deleteModel(modelId) {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/models/${modelId}`, {
      method: "DELETE",
      headers,
    });
    return this.handleResponse(response);
  }

  // Learn endpoint: train or update model with data
  async learnModel(modelId, input) {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/models/${modelId}/learn`, {
      method: "POST",
      headers,
      body: JSON.stringify(input),
    });
    return this.handleResponse(response);
  }

  // Predict endpoint for classification or regression models
  async predictModel(modelId, input) {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/models/${modelId}/predict`, {
      method: "POST",
      headers,
      body: JSON.stringify(input),
    });
    return this.handleResponse(response);
  }

  // Forecast endpoint for forecasting models
  async forecastModel(modelId, input) {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/models/${modelId}/forecast`, {
      method: "POST",
      headers,
      body: JSON.stringify(input),
    });
    return this.handleResponse(response);
  }
}

// Create a singleton instance
const kappamlAPI = new KappaMLAPI();

export default kappamlAPI;
